import { useCallback, useRef, useState } from "react";
import saveAs from "file-saver";
import useApi from "./useApi";
import { useToaster } from "./useToaster";
import { downloadPostIfPdf } from "../services/pifService";
import { PIF_PDF_MESSAGES } from "../constants/messages";
import { SEVERITIES } from "../data/constants";

/**
 * "PostIF-PIF-2026-0227.pdf". Only letters, digits, '-' and '_' survive, so no path or applicant
 * detail can reach the file name; falls back to the numeric id when the ref is missing.
 *
 * Built here rather than read from Content-Disposition: the API's CORS setup does not expose that
 * header to the browser. The rule matches the server's.
 */
export const postIfPdfFileName = (ref: string | null | undefined, postIfId: number): string =>
  `PostIF-${(ref ?? "").replace(/[^A-Za-z0-9_-]/g, "") || postIfId}.pdf`;

/**
 * One Download PDF control (Epic 83470). `downloadingId` is the id currently downloading (null
 * when idle), so a list can spin only the clicked row while disabling the rest.
 *
 * The busy guard is a ref, not the state: a fast double-click lands both clicks before React
 * re-renders, and a state check would let the second one through.
 */
export const usePostIfPdfDownload = () => {
  const { executeApi } = useApi();
  const showToaster = useToaster();
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const busyRef = useRef(false);

  const download = useCallback(
    async (postIfId: number, ref?: string | null) => {
      if (busyRef.current || !postIfId) return;
      busyRef.current = true;
      setDownloadingId(postIfId);
      try {
        // useApi shows the error toast and resolves null on failure.
        const blob = await executeApi<number, Blob>(downloadPostIfPdf, postIfId);
        if (blob) {
          // file-saver revokes its object URL itself once the download has started.
          saveAs(blob, postIfPdfFileName(ref, postIfId));
          showToaster(PIF_PDF_MESSAGES.SUCCESS, SEVERITIES.SUCCESS);
        }
      } finally {
        busyRef.current = false;
        setDownloadingId(null);
      }
    },
    [executeApi, showToaster],
  );

  return { download, downloadingId };
};
